import React, { useMemo, useState } from 'react';
import { Download, CalendarDays, Clock, Briefcase, Eye, FileText, X } from 'lucide-react';
import { Tooltip, IconButton } from '@mui/material';
import CentralizedTable from './CentralizedTable';
import UserAvatar from './UserAvatar';
import TaskDescriptionModal from './TaskDescriptionModal';
import ExportFilenamePopover from './ExportFilenamePopover';
import { cleanHtml } from '../Utils';
import * as XLSX from "xlsx";

export default function GroupedTimesheetTable({ data = [], projects = [], employees = [], isLoading = false }) {
    const [open, setOpen] = useState(false);
    const [desc, setDesc] = useState("");

    const getProjectName = (code) => {
        const project = projects?.find((el) => el?.PROJECT_CODE === code);
        return project?.PROJECT_NAME || code;
    };

    const taskTypeMap = {
        "Application Development": [
            "01-Requirement Gathering / Analysis",
            "02-Architecture / Design / Wireframe",
            "03-Coding / Development - New",
            "04-Coding / Development - Rework",
            "05-Unit Testing / Validation",
            "06-System Integration Testing",
            "07-Bug Fixing",
            "08-Deployment",
            "09-Learning / Research / Evaluation",
            "10-Documentation",
            "11-Knowledge Transfer",
            "12-Production Run",
            "13-Status calls / Meetings / Discussions",
            "14-Interview Preparation / Hiring & Interview Support",
        ],
        "DevOps & Infra": [
            "21-Requirement Gathering / Analysis",
            "22-Infrastructure Planning & Design / Wireframe",
            "23-CI/CD Pipeline Development",
            "24-Infrastructure as Code (IaC)",
            "25-Configuration & Environment Rework",
            "26-Code / Configurations Review and Testing Validation",
            "27-Security Hardening & Compliance",
            "28-Monitoring & Observability Setup",
            "29-Softwares & Application Installation /Reinstallation/Upgrades",
            "30-Production Run / Health Monitoring",
            "31-Troubleshooting & Incident Support",
            "32-Maintenance & Optimization",
            "33-Knowledge Transfer / Documentation & Runbooks",
            "34-Status calls / Meetings / Discussions",
            "35-Interview Preparation / Hiring & Interview Support",
        ],
        "Data Engineering": [
            "41-Requirement Gathering / Analysis",
            "42-Data Discovery & Pipeline Design / Wireframe",
            "43-Data Pipeline Development - New",
            "44-Database & Warehouse Structuring",
            "45-Data Pipeline Maintenance & Rework",
            "46-Curation Script Development / Enhancement",
            "47-Data Quality Scripts & Validation Checks",
            "48-SQL Code Reviews / Unit Testing / System Testing",
            "49-Data Visualization Development",
            "50-Dashboard Maintenance, Validation & Delivery",
            "51-Data Loads / Production Run / Health Monitoring",
            "52-Troubleshooting / Bug Fixing",
            "53-Performance Tuning & Optimization",
            "54-Knowledge Transfer / Documentation & Metadata",
            "55-Status calls / Meetings / Discussions",
            "56-Interview Preparation / Hiring & Interview Support",
        ]
    };

    const getDomain = (domain, taskType) => {
        if (domain) return domain;
        for (const [key, types] of Object.entries(taskTypeMap)) {
            if (types.includes(taskType)) return key;
        }
        return "";
    };

    const groupedData = useMemo(() => {
        const groups = {};
        data.forEach(row => {
            const empCode = row.CODE || row.EMP_NAME;
            if (!groups[empCode]) {
                const empInfo = employees?.find(e => e?.EMP_CODE === row?.CODE);
                groups[empCode] = {
                    empName: row.EMP_NAME,
                    empCode: row.CODE,
                    empId: empInfo ? empInfo.EMP_ID : "-",
                    rows: [],
                    totalHours: 0,
                    daysWorked: new Set(),
                    projectsWorked: new Set()
                };
            }
            groups[empCode].rows.push(row);
            groups[empCode].totalHours += Number(row.WORK_HOURS);
            groups[empCode].daysWorked.add(row.WORK_DAY);
            groups[empCode].projectsWorked.add(row.PROJECT_CODE);
        });

        return Object.values(groups).map(g => ({
            ...g,
            daysWorked: g.daysWorked.size,
            projectsWorked: g.projectsWorked.size,
            totalHours: g.totalHours
        })).sort((a, b) => (a.empName || "").localeCompare(b.empName || ""));
    }, [data, employees]);

    const handleExport = (employeeGroup = null, customFilename = null) => {
        const recordsToExport = employeeGroup ? employeeGroup.rows : data;
        
        if (!recordsToExport || recordsToExport.length === 0) return;

        // 1. Summary Sheet Data
        const groupsToExport = employeeGroup ? [employeeGroup] : groupedData;
        const summaryData = groupsToExport?.map(g => ({
            "Employee ID": g.empId,
            "Employee Name": g.empName || "",
            "Days Worked": g.daysWorked,
            "Projects Worked": g.projectsWorked,
            "Total Hours": g.totalHours
        }));

        // 2. Details Sheet Data
        const detailsData = recordsToExport?.map(row => {
            const empInfo = employees?.find(e => e?.EMP_CODE === row?.CODE);
            return {
            "Employee ID": empInfo ? empInfo.EMP_ID : "-",
            "Employee Name": row.EMP_NAME || "",
            "Project": getProjectName(row.PROJECT_CODE),
            "Date": row.WORK_DAY || "",
            "Hours": row.WORK_HOURS,
            "Task": row.TASK || "",
            "Task Type": row.Task_Type || "",
            "Domain": getDomain(row.Domain, row.Task_Type),
            "Description": cleanHtml(row.RawDescription || row.Description || "")
            };
        });

        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        const detailsSheet = XLSX.utils.json_to_sheet(detailsData);
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
        XLSX.utils.book_append_sheet(workbook, detailsSheet, "Details");
        
        const dateSuffix = new Date().toISOString().split('T')[0];
        const defaultName = employeeGroup ? `Timesheet_Export_${(employeeGroup.empName || "").replace(/\s+/g, '_')}_${dateSuffix}.xlsx` : `Timesheet_Export_All_${dateSuffix}.xlsx`;
        XLSX.writeFile(workbook, customFilename || defaultName);
    };

    const renderTruncatedCell = (params) => {

    return (
            <Tooltip title={params.value || ""} placement="top-start" arrow>
                <div className="w-full truncate text-slate-600 font-medium text-[13px]">
                    {params.value || "-"}
                </div>
            </Tooltip>
        );
    };

    const innerColumns = [
        {
            headerName: "Date",
            field: "WORK_DAY",
            sortable: true,
            width: 120,
            renderCell: renderTruncatedCell,
        },
        {
            headerName: "Project",
            field: "PROJECT_CODE",
            sortable: true,
            width: 200,
            valueGetter: (params) => getProjectName(params.row?.PROJECT_CODE || params.value),
            renderCell: renderTruncatedCell,
        },
        {
            headerName: "Hours",
            field: "WORK_HOURS",
            sortable: true,
            width: 100,
            renderCell: (params) => (
                <Tooltip title={params.row.WORK_HOURS + "h"} placement="top" arrow>
                    <span className="inline-flex items-center justify-center px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-bold whitespace-nowrap">
                        {params.row.WORK_HOURS}hrs
                    </span>
                </Tooltip>
            )
        },
        {
            headerName: "Task",
            field: "TASK",
            sortable: true,
            width: 200,
            renderCell: renderTruncatedCell,
        },
        {
            headerName: "Task Type",
            field: "Task_Type",
            sortable: true,
            width: 180,
            renderCell: renderTruncatedCell,
        },
        {
            headerName: "Domain",
            field: "Domain",
            sortable: true,
            width: 180,
            valueGetter: (params) => getDomain(params.row.Domain, params.row.Task_Type),
            renderCell: renderTruncatedCell,
        },
        {
            headerName: "Description",
            field: "Description",
            sortable: false,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <div className="flex items-center">
                    <Tooltip title="View Description">
                        <button onClick={() => { setDesc(params.row.RawDescription || params.row.Description); setOpen(true); }} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors bg-white shadow-sm">
                            <Eye size={14} strokeWidth={2.5} />
                        </button>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const outerColumns = [
        {
            headerName: "Employee",
            field: "empName",
            sortable: true,
            minWidth: 250,
            renderCell: (params) => (
                <div className="flex items-center gap-3">
                    <UserAvatar name={params.row.empName} size={36} />
                    <div className="flex flex-col justify-center items-start min-w-0 flex-1 overflow-hidden">
                        <Tooltip title={params.row.empName} placement="top" arrow disableInteractive>
                            <h3 className="text-[14px] font-semibold text-slate-800 m-0 leading-tight truncate cursor-default max-w-full">{params.row.empName}</h3>
                        </Tooltip>
                        <Tooltip title={params.row.empId} placement="bottom" arrow disableInteractive>
                            <p className="text-[11px] font-medium text-slate-500 m-0 uppercase tracking-widest mt-0.5 truncate cursor-default max-w-full">{params.row.empId}</p>
                        </Tooltip>
                    </div>
                </div>
            )
        },
        {
            headerName: "Days Worked",
            field: "daysWorked",
            sortable: true,
            width: 140,
            renderCell: (params) => (
                <div className="flex items-center text-slate-700 font-medium text-[13px]">
                    {params.row.daysWorked}
                </div>
            )
        },
        {
            headerName: "Total Hours",
            field: "totalHours",
            sortable: true,
            width: 140,
            renderCell: (params) => (
                <Tooltip title={params.row.WORK_HOURS + "h"} placement="top" arrow>
                    <span className="inline-flex items-center justify-center px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-bold whitespace-nowrap">
                        {params.row.totalHours} hrs
                    </span>
                </Tooltip>
            )
        },
        {
            headerName: "Projects",
            field: "projectsWorked",
            sortable: true,
            width: 120,
            renderCell: (params) => (
                <div className="flex items-center text-slate-700 font-medium text-[13px]">
                    {params.row.projectsWorked}
                </div>
            )
        },
        {
            headerName: "Actions",
            field: "Actions",
            sortable: false,
            width: 80,
            renderCell: (params) => (
                <div className='flex items-center'>
                    <ExportFilenamePopover 
                        defaultFilename={`Timesheet_Export_${(params.row.empName || "").replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`} 
                        onExport={(filename) => handleExport(params.row, filename)}
                    >
                        <Tooltip title={`Export ${params.row.empName}'s Entries`} arrow>
                            <IconButton size="small" className="hover:bg-slate-100 transition-colors">
                                <Download size={18} className="text-slate-500 hover:text-slate-700 transition-colors" />
                            </IconButton>
                        </Tooltip>
                    </ExportFilenamePopover>
                </div>
            )
        }
    ];

    const dummyRows = Array(10).fill(0).map((_, i) => ({
        empCode: `skeleton-${i}`,
        empName: '',
        daysWorked: '',
        totalHours: '',
        projectsWorked: '',
        Actions: ''
    }));

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
            <CentralizedTable
                key={isLoading ? 'loading' : 'loaded'}
                rows={isLoading ? dummyRows : groupedData}
                columns={outerColumns}
                getRowId={(row) => row.empCode}
                isLoading={isLoading}
                enableExport={false}
                positionExpandColumn="first"
                renderTopToolbarCustomActions={() => (
                    <div className="flex items-center space-x-3 py-2.5">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                            <CalendarDays size={18} />
                        </div>
                        <div>
                            <h3 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">Timesheet Overview</h3>
                            <p className="text-xs text-slate-500 m-0 mt-0.5">Manage and view all employee timesheet entries</p>
                        </div>
                    </div>
                )}
                customInternalActions={() => (
                    <ExportFilenamePopover defaultFilename={`Timesheet_Export_All_${new Date().toISOString().split('T')[0]}.xlsx`} onExport={(filename) => handleExport(null, filename)}>
                        <Tooltip title="Export All Entries" arrow>
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                                <Download size={18} />
                            </IconButton>
                        </Tooltip>
                    </ExportFilenamePopover>
                )}
                muiTablePaperProps={{ elevation: 0, sx: { border: 'none', borderRadius: 0 } }}
                muiDetailPanelProps={{ sx: { p: 0 } }}
                muiExpandAllButtonProps={{ disabled: isLoading }}
                muiExpandButtonProps={{ disabled: isLoading }}
                initialState={{
                    pagination: { pageSize: 10, pageIndex: 0 },
                    expanded: {},
                }}
                    muiTableContainerProps={{
                            sx: {
                                minHeight: "auto",
                                height: 'auto',
                                flex: 1,
                                overflow: 'auto',
                            },
                        }}
                renderDetailPanel={({ row }) => (
                    <div className="p-3 sm:p-3 bg-slate-100 border-t border-b border-slate-200" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
                        <div className="bg-slate-50/50 rounded-xl shadow-[inset_0_1px_3px_rgba(0,0,0,0.02)] border border-slate-200 overflow-hidden">
                            <CentralizedTable
                                rows={row.original.rows}
                                columns={innerColumns}
                                getRowId={(innerRow) => innerRow.CODE + innerRow.WORK_DAY + innerRow.PROJECT_CODE + innerRow.TASK + innerRow.WORK_HOURS + Math.random()}
                                enableTopToolbar={false}
                                enableBottomToolbar={true}
                                muiTableProps={{ sx: { tableLayout: 'fixed' } }}
                                muiTablePaperProps={{ elevation: 0, sx: { border: 'none', borderRadius: 0, bgcolor: 'transparent' } }}
                                muiTableContainerProps={{ sx: { overflowX: 'auto', overflowY: 'hidden' } }}
                                initialState={{
                                    pagination: { pageSize: 5, pageIndex: 0 },
                                }}
                                pageSizeOptions={[5, 10, 25]}
                            />
                        </div>
                    </div>
                )}
            />

            <TaskDescriptionModal
                isOpen={open}
                onClose={() => setOpen(false)}
                description={desc}
            />
        </div>
    );
}



